/**
 * Lectura (pura) de las respuestas de la pestaña Análisis (fase 1): público (edad, género, país,
 * región), plataformas / ubicaciones, interacciones y público configurado de los conjuntos.
 * Todas reciben el envelope de `httpClient` (`{ data, _httpOk… }`; la API manda
 * `{ message, data, owner_user_id }`) y nunca lanzan: lo que falta queda en `null` / listas vacías.
 */
import {
  metaAgeLabel, metaAgeRank, metaAdSetStatusLabel, metaCountryLabel, metaGenderKey, metaGenderLabel,
  metaPlacementLabel, metaPlatformLabel, metaPositionName, metaRegionLabel,
} from './metaLabels';
import { metaNum, stripMetaTerrioMark } from './metaFormat';

const dataOf = (res) => (res?.data && typeof res.data === 'object' ? res.data : {});
const list = (value) => (Array.isArray(value) ? value : []);
const text = (value) => (value == null ? '' : String(value).trim());

/** Fila de un desglose (`build_breakdown_row` de la API) sin transformar nombres. */
export function readMetaBreakdownRow(raw) {
  return {
    spend: metaNum(raw?.spend),
    impressions: metaNum(raw?.impressions),
    clicks: metaNum(raw?.clicks),
    linkClicks: metaNum(raw?.link_clicks),
    contacts: metaNum(raw?.contacts),
    costPerContact: metaNum(raw?.cost_per_contact),
    ctrPct: metaNum(raw?.ctr_pct),
    cpm: metaNum(raw?.cpm),
    cpc: metaNum(raw?.cpc),
  };
}

const sumOf = (rows, key) => rows.reduce((acc, r) => acc + (r[key] || 0), 0);

/** Más contactos primero; si empatan, más clics y más impresiones. */
const byContacts = (a, b) => (b.contacts || 0) - (a.contacts || 0)
  || (b.clicks || 0) - (a.clicks || 0)
  || (b.impressions || 0) - (a.impressions || 0);

/** Qué mide cada barra: contactos; si no hubo ninguno en todo el período, impresiones. */
function barMetric(rows) {
  return sumOf(rows, 'contacts') > 0 ? 'contacts' : 'impressions';
}

const common = (data) => ({
  currency: text(data.currency).toUpperCase() || null,
  mixedCurrencies: Boolean(data.mixed_currencies),
  limitedBySpecialCategory: Boolean(data.limited_by_special_category),
});

/**
 * Edad y género (`breakdown=age_gender`). Las edades salen ordenadas por tramo (la API ordena por
 * gasto); el género `null` es «Desconocido».
 */
export function readMetaAgeGender(res) {
  const data = dataOf(res);
  const block = data.age_gender && typeof data.age_gender === 'object' ? data.age_gender : null;
  if (!block) return { available: false, ages: [], genders: [], metric: 'contacts', ...common(data) };
  const ages = list(block.by_age).map((r) => ({
    key: text(r.age) || 'unknown', label: metaAgeLabel(r.age), ...readMetaBreakdownRow(r),
  })).sort((a, b) => metaAgeRank(a.key) - metaAgeRank(b.key));
  const genderRows = new Map();
  list(block.by_gender).forEach((r) => {
    const key = metaGenderKey(r.gender);
    const row = readMetaBreakdownRow(r);
    const prev = genderRows.get(key);
    // `null` y `unknown` son lo mismo: se juntan en «Desconocido».
    genderRows.set(key, prev ? {
      ...prev,
      contacts: (prev.contacts || 0) + (row.contacts || 0),
      impressions: (prev.impressions || 0) + (row.impressions || 0),
      clicks: (prev.clicks || 0) + (row.clicks || 0),
      spend: prev.spend == null || row.spend == null ? null : prev.spend + row.spend,
      costPerContact: null,
    } : { key, label: metaGenderLabel(key), ...row });
  });
  const genders = [...genderRows.values()].sort(byContacts);
  const metric = barMetric(ages);
  const total = sumOf(genders, metric);
  return {
    available: true,
    ages,
    genders: genders.map((g) => ({ ...g, value: g[metric] || 0, pct: total > 0 ? ((g[metric] || 0) / total) * 100 : null })),
    metric,
    ...common(data),
  };
}

/**
 * País y región (`breakdown=geo`). `geoPending`: la cobertura dice que todavía no se trajeron
 * (geo se carga solo en la sincronización programada de las 6:00). Cobertura =
 * `geo_data_coverage ?? data_coverage`.
 */
export function readMetaGeo(res) {
  const data = dataOf(res);
  const rows = (block, key, labelOf, unknownLabel) => list(block?.rows).map((r) => {
    const isUnknown = r?.is_unknown === true;
    return { key: text(r[key]) || (isUnknown ? 'unknown' : ''), label: isUnknown ? unknownLabel : labelOf(r[key]), isUnknown, ...readMetaBreakdownRow(r) };
  }).filter((r) => !isEmptyUnknownGeo(r));
  const cov = data.geo_data_coverage ?? data.data_coverage;
  const regionContacts = readMetaRegionContacts(data.region);
  return {
    available: Boolean(data.country || data.region),
    countries: rows(data.country, 'country', metaCountryLabel, 'Desconocido').sort(byContacts),
    regions: rows(data.region, 'region', metaRegionLabel, 'Desconocida').sort(regionContacts.complete ? byContacts : byImpressions),
    regionContacts,
    geoPending: Boolean(cov && cov.complete === false),
    ...common(data),
  };
}

/**
 * Fila «Desconocida» / «Desconocido» sin nada que mostrar: impresiones, clics y gasto en 0 (o sin dato).
 * Se oculta en países y regiones; con algún valor se sigue mostrando. Las filas conocidas no se tocan.
 */
const isEmptyUnknownGeo = (r) => r.isUnknown && !(r.impressions || 0) && !(r.clicks || 0) && !(r.spend || 0);

/** Más impresiones primero; si empatan, más clics. */
const byImpressions = (a, b) => (b.impressions || 0) - (a.impressions || 0) || (b.clicks || 0) - (a.clicks || 0);

/**
 * ¿Los contactos por región están completos? (27/10) Meta no informa las conversaciones de WhatsApp
 * por región: con `contacts_complete: false` la región se muestra por impresiones, sin contactos ni
 * costo por contacto, y con `contacts_note` (texto de la API) abajo. Una API sin el campo = completo
 * (comportamiento anterior).
 */
export function readMetaRegionContacts(block) {
  const b = block && typeof block === 'object' ? block : {};
  return {
    complete: b.contacts_complete !== false,
    scope: text(b.contacts_scope) || null,
    note: text(b.contacts_note) || null,
  };
}

/** Plataformas y ubicaciones (`breakdown=platform_position`). */
export function readMetaPlatforms(res) {
  const data = dataOf(res);
  const block = data.platform_position && typeof data.platform_position === 'object' ? data.platform_position : null;
  if (!block) return { available: false, platforms: [], placements: [], metric: 'contacts', ...common(data) };
  const platforms = list(block.by_platform).map((r) => ({
    key: text(r.publisher_platform), label: metaPlatformLabel(r.publisher_platform), ...readMetaBreakdownRow(r),
  })).sort(byContacts);
  const placements = list(block.rows).map((r) => ({
    key: `${text(r.publisher_platform)}/${text(r.platform_position)}`,
    platformKey: text(r.publisher_platform),
    label: metaPlacementLabel(r.publisher_platform, r.platform_position),
    positionLabel: metaPositionName(r.platform_position, r.publisher_platform),
    ...readMetaBreakdownRow(r),
  }));
  return { available: true, platforms, placements, metric: barMetric(platforms), ...common(data) };
}

/** Ubicaciones ordenadas: `impressions` = «Más vistas»; `contacts` = «Más contactos». */
export function sortMetaPlacements(placements, mode) {
  const copy = [...placements];
  if (mode === 'impressions') {
    return copy.sort((a, b) => (b.impressions || 0) - (a.impressions || 0) || byContacts(a, b));
  }
  return copy.sort((a, b) => byContacts(a, b));
}

// ── Interacciones ───────────────────────────────────────────────────────────

/**
 * Interacciones de `getcampaignmetrics` (de una campaña, o de todo el espacio sin filtro): el
 * mismo bloque `metrics`. `total_interactions` (= `post_engagement`) NO es la suma de sus partes
 * (reacciones + comentarios…): no se calcula «otros» ni se apila.
 * El alcance no se suma entre días ni anuncios: casi siempre viene `null` (y no se muestra).
 */
export function readMetaInteractions(res) {
  const data = dataOf(res);
  const m = data.metrics && typeof data.metrics === 'object' ? data.metrics : null;
  if (!m) return { available: false, ...common(data) };
  const eng = m.engagement && typeof m.engagement === 'object' ? m.engagement : {};
  const v = m.video && typeof m.video === 'object' ? m.video : {};
  const pctOfPlays = v.pct_of_plays && typeof v.pct_of_plays === 'object' ? v.pct_of_plays : {};
  const retention = v.retention_available
    ? [25, 50, 75, 100].map((p) => ({ key: `p${p}`, label: `${p} %`, count: metaNum(v[`p${p}`]), pct: metaNum(pctOfPlays[`p${p}`]) }))
    : null;
  return {
    available: true,
    ...common(data),
    impressions: metaNum(m.impressions),
    reach: metaNum(m.reach),
    totalInteractions: metaNum(eng.post_engagement),
    costPerEngagement: metaNum(m.cost_per_engagement),
    reactions: metaNum(eng.reactions),
    comments: metaNum(eng.comments),
    shares: metaNum(eng.shares),
    saves: metaNum(eng.saves),
    pageLikes: metaNum(eng.page_likes),
    linkClicks: metaNum(m.link_clicks),
    clicks: metaNum(m.clicks),
    outboundClicks: metaNum(m.outbound_clicks),
    landingPageViews: metaNum(m.landing_page_views),
    video: {
      plays: metaNum(v.plays),
      views3s: metaNum(v.views_3s),
      thruplays: metaNum(v.thruplays),
      avgWatchTimeS: metaNum(v.avg_watch_time_s),
      costPerThruplay: metaNum(m.cost_per_thruplay),
      // Las dos tasas son sobre IMPRESIONES (no sobre reproducciones).
      hookRatePct: metaNum(v.hook_rate_pct),
      thruplayRatePct: metaNum(v.thruplay_rate_pct),
      retention,
    },
    hasVideo: (metaNum(v.plays) || 0) > 0,
  };
}

// ── Público configurado (conjuntos de anuncios) ─────────────────────────────

/** «A», «A y B», «A, B y C» (con «e» delante de una «i»). */
export function joinMetaList(items) {
  const xs = items.filter(Boolean);
  if (xs.length <= 1) return xs.join('');
  const last = xs[xs.length - 1];
  const conj = /^h?i/i.test(last) && !/^hu/i.test(last) ? 'e' : 'y';
  return `${xs.slice(0, -1).join(', ')} ${conj} ${last}`;
}

const MAX_INTERESTS = 5;

/**
 * Resumen en líneas del público que el anunciante CONFIGURÓ (`targeting`): no es quién respondió.
 * @returns {string[]} 0 a 2 líneas listas para mostrar.
 */
export function metaTargetingLines(targeting) {
  const t = targeting && typeof targeting === 'object' ? targeting : {};
  const first = [];
  const min = metaNum(t.age_min);
  const max = metaNum(t.age_max);
  if (min != null && max != null) first.push(`${min} a ${max}${max >= 65 ? '+' : ''} años`);
  else if (min != null) first.push(`Desde ${min} años`);
  else if (max != null) first.push(`Hasta ${max} años`);
  // «Mujeres y hombres»: solo la primera con mayúscula.
  const genders = list(t.genders).map((g, i) => (i === 0 ? metaGenderLabel(g) : metaGenderLabel(g).toLowerCase()));
  if (genders.length) first.push(joinMetaList(genders));
  const countries = list(t.countries).map((c) => metaCountryLabel(c));
  const places = [...list(t.regions).map((r) => metaRegionLabel(r)), ...list(t.cities).map(text)].filter(Boolean);
  if (countries.length) first.push(`${joinMetaList(countries)}${places.length ? ` (${places.join(', ')})` : ''}`);
  else if (places.length) first.push(places.join(', '));
  const second = [];
  const interests = list(t.interests).map(text).filter(Boolean);
  if (interests.length) {
    const shown = interests.slice(0, MAX_INTERESTS);
    const extra = interests.length - shown.length;
    second.push(`Intereses: ${shown.join(', ')}${extra > 0 ? ` y ${extra} más` : ''}`);
  }
  const behaviors = list(t.behaviors).map(text).filter(Boolean);
  if (behaviors.length) second.push(`Comportamientos: ${behaviors.join(', ')}`);
  const platforms = list(t.publisher_platforms).map((p) => metaPlatformLabel(p));
  const positions = [...new Set(list(t.positions).map((p) => metaPositionName(p)).filter(Boolean))];
  if (platforms.length) second.push(`${joinMetaList(platforms)}${positions.length ? ` (${positions.join(', ')})` : ''}`);
  if (t.advantage_audience === true) second.push('Audiencia Advantage activa');
  const custom = metaNum(t.custom_audiences_count);
  if (custom > 0) second.push(`${custom} ${custom === 1 ? 'público personalizado' : 'públicos personalizados'}`);
  return [first.join(' · '), second.join(' · ')].filter(Boolean);
}

/** Conjuntos de `getmetaadsets` (ya vienen por gasto del período, de mayor a menor). */
export function readMetaAdSets(res) {
  const data = dataOf(res);
  return {
    adsets: list(data.adsets).map((a) => ({
      id: text(a.adset_id),
      name: text(stripMetaTerrioMark(a.name)) || `Conjunto ${text(a.adset_id)}`,
      campaignId: text(a.campaign_id),
      campaignName: text(stripMetaTerrioMark(a.campaign_name)),
      statusLabel: metaAdSetStatusLabel(a.effective_status || a.status),
      isActive: text(a.effective_status || a.status).toUpperCase() === 'ACTIVE',
      adsCount: metaNum(a.ads_count),
      lines: metaTargetingLines(a.targeting),
    })).filter((a) => a.id),
    count: metaNum(data.adsets_count),
  };
}
